import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from './AuthenticationService';
import { AppConfig } from '../app.config';
import { BasicResponse } from '../responses/basicResponse';
import { MoovDefinition } from '../models/moovDefinition';
import { CreateMoovDefinitionRequest } from '../requests/createMoovDefinitionRequest';
import { UpdateMoovDefinitionRequest } from '../requests/updateMoovDefinitionRequest';
import { DeleteMoovDefinitionRequest } from '../requests/deleteMoovDefinitionRequest';
import { UndeleteMoovDefinitionRequest } from '../requests/undeleteMoovDefinitionRequest';
import { MoovConfiguration } from '../models/moovConfiguration';
import { CreateMoovConfigurationRequest } from '../requests/createMoovConfigurationRequest';
import { UpdateMoovConfigurationRequest } from '../requests/updateMoovConfigurationRequest';
import { DeleteMoovConfigurationRequest } from '../requests/deleteMoovConfigurationRequest';
import { UndeleteMoovConfigurationRequest } from '../requests/undeleteMoovConfigurationRequest';
import { AdoptMoovDefinitionRequest } from '../requests/adoptMoovDefinitionRequest';
import { AbandonMoovDefinitionRequest } from '../requests/abandonMoovDefinitionRequest';
import { ApproveMoovStepInstanceRequest } from '../requests/approveMoovStepInstanceRequest';
import { SuspendMoovInstanceRequest } from '../requests/suspendMoovInstanceRequest';
import { ResumeMoovInstanceRequest } from '../requests/resumeMoovInstanceRequest';
import { DeleteMoovInstanceRequest } from '../requests/deleteMoovInstanceRequest';
import { MoovStepCustomerParameters } from '../models/moovStepCustomerParameters';
import { CreateMoovStepCustomerParametersRequest } from '../requests/createMoovStepCustomerParametersRequest';
import { UpdateMoovStepCustomerParametersRequest } from '../requests/updateMoovStepCustomerParametersRequest';
import { DeleteMoovStepCustomerParametersRequest } from '../requests/deleteMoovStepCustomerParametersRequest';
import { PublishMoovDefinitionRequest } from '../requests/publishMoovDefinitionRequest';
import { CreateMoovVariableRequest } from '../requests/createMoovVariableRequest';
import { MoovVariable } from '../models/moovVariable';
import { UpdateMoovVariableRequest } from '../requests/updateMoovVariableRequest';
import { DeleteMoovVariableRequest } from '../requests/deleteMoovVariableRequest';
import { UploadFileRequest } from '../requests/uploadFileRequest';
import { ActionItemActionRequest } from '../requests/actionItemActionRequest';
import { ResolveMoovInstanceCollisionRequest } from '../requests/resolveMoovInstanceCollisionRequest';
import { ExecuteMoovStepInstanceRequest } from '../requests/executeMoovStepInstanceRequest';
import { RunFailedMoovStepInstanceRequest } from '../requests/runFailedMoovStepInstanceRequest';
import { ActivateAdoptedMoovDefinitionRequest } from '../requests/activateAdoptedMoovDefinitionRequest';
import { PremierServiceSchedule } from '../models/premierServiceSchedule';
import { CreatePremierServiceScheduleRequest } from '../requests/createPremierServiceScheduleRequest';
import { UpdateFileRequest } from '../requests/updateFileRequest';
import { RestoreFileRequest } from '../requests/restoreFileRequest';
import { DeleteQBEntriesForMoovStepInstanceRequest } from '../requests/deleteQBEntriesForMoovStepInstanceRequest';
import { StartMoovInstanceRequest } from '../requests/startMoovInstanceRequest';
import { DeleteXeroEntriesForMoovStepInstanceRequest } from '../requests/deleteXeroEntriesForMoovStepInstanceRequest';
import { DeleteMicrosoftCalendarEntriesForMoovStepInstanceRequest } from '../requests/deleteMicrosoftCalendarEntriesForMoovStepInstanceRequest';
import { MoovDefinitionCustomerAdoption } from '../models/moovDefinitionCustomerAdoption';
import { UpdateMoovDefinitioncustomerAdoptionRequest as UpdateMoovDefinitionCustomerAdoptionRequest } from '../requests/updateMoovDefinitionCustomerAdoptionRequest';
import { ToggleMoovExecutionStatusRequest } from '../requests/toggleMoovExecutionStatusRequest';
import { ReprocessFilesRequest } from '../requests/reprocessFilesRequest';
import { LoggingService } from './LoggingService';
import { UpdateMoovConfigurationUsagePriceOverrideRequest } from '../requests/updateMoovConfigurationUsagePriceOverrideRequest';
import { MoovStep } from '../models/moovStep';
import { MoovInstancesRequest } from '../requests/moovInstancesRequest';
import { MoovDefinitionsRequest } from '../requests/moovDefinitionsRequest';

@Injectable({
  providedIn: 'root'
})
export class MoovManagementApiService {

  constructor(private httpClient: HttpClient, private appConfig:AppConfig, private authService:AuthenticationService, private logger:LoggingService) { }

  get serviceURL():string {
    return this.appConfig.getConfig("moovmanagementapi_external_url") + '/api/moovmanagement/';
  }
  get optionsJson(){
    return this.appConfig.optionsJson;
  }
  get optionsForm(){
    return this.appConfig.optionsForm;
  }

  //#region moov execution status
  public getMoovExecutionStatus(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting moov execution status");
    return this.httpClient.get<any>(this.serviceURL+`moovExecutionStatus` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public toggleMoovExecutionStatus(requestorId:number, executingMoovs:boolean):Observable<any>{
    let r = new ToggleMoovExecutionStatusRequest();
    r.requestorId = requestorId;
    r.executingMoovs = executingMoovs;
    return this.httpClient.put<any>(this.serviceURL+`toggleMoovExecutionStatus`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion
  
  //#region moov definitions
  public getMoovStepDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting moov step definitions");
    return this.httpClient.get<any>(this.serviceURL+`moovStepDefinitions` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting moov definitions");
    return this.httpClient.get<any>(this.serviceURL+`moovDefinitions` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovDefinitionById(id:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting moov definition by id: " + id);
    return this.httpClient.get<any>(this.serviceURL+`moovDefinitionById/` + id + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovDefinitionsByIds(moovDefinitionIdList:number[], requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    let r = new MoovDefinitionsRequest();
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovDefinitionIdList = moovDefinitionIdList;
    return this.httpClient.post<any>(this.serviceURL+`moovDefinitionsById`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAdoptedMoovDefinitions(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting adopted moov definitions for entityId " + requestorEntityId);
    return this.httpClient.get<any>(this.serviceURL+`adoptedMoovDefinitions` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovDefinitionCustomerAdoption(moovDefinitionId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting moov definition customer adoption");
    return this.httpClient.get<any>(this.serviceURL+`moovDefinitionCustomerAdoption/` + moovDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateMoovDefinitionCustomerAdoption(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, mdca:MoovDefinitionCustomerAdoption):Observable<any>{
    let r = new UpdateMoovDefinitionCustomerAdoptionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionCustomerAdoption = MoovDefinitionCustomerAdoption.mapToDTO(mdca);
    return this.httpClient.put<any>(this.serviceURL+`updateMoovDefinitionCustomerAdoption`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getActiveMoovDefinitionsCount(): Observable<any>{
    return this.httpClient.get<any>(this.serviceURL + `activeMoovDefinitionsCount`, this.optionsJson).pipe(catchError((error:any):any=>{this.handleError(error); return of(this.createBasicResponseError(error))}));
  }

  public activateAdoptedMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovDefinitionId:number, active:boolean){
    let r = new ActivateAdoptedMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionId=moovDefinitionId;
    r.active=!active; //reverse boolean to change the current status
    return this.httpClient.post<any>(this.serviceURL + `activateAdoptedMoovDefinition`, r, this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error))}));
  }

  public createMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, md:MoovDefinition):Observable<any>{
    let r = new CreateMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinition = MoovDefinition.mapToDTO(md);
    return this.httpClient.post<any>(this.serviceURL+`createMoovDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, md:MoovDefinition):Observable<any>{
    let r = new UpdateMoovDefinitionRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinition = MoovDefinition.mapToDTO(md);
    return this.httpClient.put<any>(this.serviceURL+`updateMoovDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovDefinitionId:number){
    this.logger.log("Deleting moov definition");
    let r=new DeleteMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionId = moovDefinitionId;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteMoovDefinition`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovDefinitionId:number){
    this.logger.log("Undeleting moov definition");
    let r=new UndeleteMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionId = moovDefinitionId;
    return this.httpClient.put<any>(this.serviceURL+'undeleteMoovDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public publishMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovDefinitionId:number, published:boolean){
    this.logger.log("Publish moov definition");
    let r=new PublishMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionId = moovDefinitionId;
    r.published=published;
    return this.httpClient.put<any>(this.serviceURL+ 'publishMoovDefinition', r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public testMoovStep(ms:MoovStep){
    const dto=MoovStep.mapToDTO(ms);
    return this.httpClient.post<any>(this.serviceURL+`testMoovStep`, dto, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public adoptMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovDefinitionId:number):Observable<any>{
    let r = new AdoptMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionId = moovDefinitionId;
    return this.httpClient.post<any>(this.serviceURL+`adoptMoovDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public abandonMoovDefinition(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovDefinitionId:number):Observable<any>{
    let r = new AbandonMoovDefinitionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovDefinitionId = moovDefinitionId;
    return this.httpClient.post<any>(this.serviceURL+`abandonMoovDefinition`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createMoovStepCustomerParameters(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, mscp:MoovStepCustomerParameters):Observable<any>{
    let r = new CreateMoovStepCustomerParametersRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovStepCustomerParameters = MoovStepCustomerParameters.mapToDTO(mscp);
    return this.httpClient.post<any>(this.serviceURL+`createMoovStepCustomerParameters`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateMoovStepCustomerParameters(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, mscp:MoovStepCustomerParameters):Observable<any>{
    let r = new UpdateMoovStepCustomerParametersRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovStepCustomerParameters = MoovStepCustomerParameters.mapToDTO(mscp);
    return this.httpClient.put<any>(this.serviceURL+`updateMoovStepCustomerParameters`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteMoovStepCustomerParameters(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, mscpId:number):Observable<any>{
    let r = new DeleteMoovStepCustomerParametersRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovStepCustomerParametersId = mscpId;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteMoovStepCustomerParameters`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateMoovConfigurationUsagePriceOverride(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovConfigurationId:number, usagePriceOverride:number):Observable<any>{
    let r = new UpdateMoovConfigurationUsagePriceOverrideRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.moovConfigurationId=moovConfigurationId;
    r.usagePriceOverride = usagePriceOverride;
    return this.httpClient.put<any>(this.serviceURL+`updateMoovConfigurationUsagePriceOverride`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovInformationForBillingBucket(billingBucketId:number): Observable<any>{
    return this.httpClient.get<any>(this.serviceURL + `moovInformationForBillingBucket/` + billingBucketId, this.optionsJson).pipe(catchError((error:any):any=>{this.handleError(error); return of(this.createBasicResponseError(error))}));
  }
  //#endregion
 
  //#region moov configurations
  public getMoovConfigurations(moovDefinitionId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting moov configurations");
    return this.httpClient.get<any>(this.serviceURL+`moovConfigurations/` + moovDefinitionId +  (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getActiveMoovConfigurationsCount():Observable<any>{
    return this.httpClient.get<any>(this.serviceURL + `activeMoovConfigurationsCount`, this.optionsJson).pipe(catchError((error:any):any=>{this.handleError(error); return of(this.createBasicResponseError(error))}));
  }

  public getMoovConfigurationStatusReport(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovConfigurationStatusReport` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovConfigurationsInStatus(status:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovConfigurationsInStatus/` + status + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getMoovConfigurationsForClient(clientId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovConfigurationsForClient/` + clientId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

   public getMoovConfigurationsMatchingName(filename:string, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovConfigurationsMatchingName/` + filename + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getExternalFileUploadInformation(uniqueIdentifier:string){
    this.logger.log("Getting external file upload information");
    return this.httpClient.get<any>(this.serviceURL+`externalFileUploadInformation/` + uniqueIdentifier, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public createMoovConfiguration(requestorId:number, mc:MoovConfiguration):Observable<any>{
    let r = new CreateMoovConfigurationRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=mc.customerId;
    r.requestorEntityType = "C";
    r.moovConfiguration = MoovConfiguration.mapToDTO(mc);
    return this.httpClient.post<any>(this.serviceURL+`createMoovConfiguration`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateMoovConfiguration(requestorId:number, mc:MoovConfiguration):Observable<any>{
    let r = new UpdateMoovConfigurationRequest();
    r.requestorId = requestorId;
    r.requestorEntityId=mc.customerId;
    r.requestorEntityType = "C";
    r.moovConfiguration = MoovConfiguration.mapToDTO(mc);
    return this.httpClient.put<any>(this.serviceURL+`updateMoovConfiguration`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteMoovConfiguration(requestorId:number, mc:MoovConfiguration){
    this.logger.log("Deleting moov configuration");
    let r=new DeleteMoovConfigurationRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=mc.customerId;
    r.requestorEntityType = "C";
    r.moovConfigurationId = mc.id!;
    r.physicalDelete = false;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteMoovConfiguration`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public undeleteMoovConfiguration(requestorId:number, mc:MoovConfiguration){
    this.logger.log("Undeleting moov configuration");
    let r=new UndeleteMoovConfigurationRequest();
    r.requestorId=requestorId;
    r.requestorEntityId=mc.customerId;
    r.requestorEntityType = "C";
    r.moovConfigurationId = mc.id!;
    return this.httpClient.put<any>(this.serviceURL+'undeleteMoovConfiguration', r, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region moov instances
  public uploadFiles(files:File[], addHeaderIfMissing:boolean){
    let r = new UploadFileRequest();
    r.addHeaderIfMissing=addHeaderIfMissing;
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.customerContext?.id;
    r.requestorEntityType=this.authService.customerContext?"C":undefined;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    files.forEach(f=> { formData.append('files', f, f.name); });
    this.logger.log("Uploading" + files.length +  "files");
    return this.httpClient.post<any>(this.serviceURL+`uploadFile`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public uploadFileFromExternal(files:File[], addHeaderIfMissing:boolean, externalUploaderName:string, externalUploaderEmail:string, moovConfigurationId:number, customerId:number){
    let r = new UploadFileRequest();
    r.addHeaderIfMissing=addHeaderIfMissing;
    r.requestorId=-1;
    r.requestorEntityId=customerId;
    r.requestorEntityType = "C";
    r.moovConfigurationId=moovConfigurationId;
    r.externalUploaderName=externalUploaderName;
    r.externalUploaderEmail=externalUploaderEmail;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    files.forEach(f=> { formData.append('files', f, f.name); });
    this.logger.log("Uploading" + files.length +  "files");
    return this.httpClient.post<any>(this.serviceURL+`uploadFileFromExternal`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public reprocessFiles(fileIds:number[], filenameStartsWith:string, filenameContains:string){
    let r = new ReprocessFilesRequest();
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.customerContext?.id;
    r.requestorEntityType=this.authService.customerContext?"C":undefined;
    r.fileIds=fileIds;
    r.filenameStartsWith=filenameStartsWith;
    r.filenamecontains=filenameContains;
    this.logger.log("Reprocessing" + fileIds.length +  "files");
    return this.httpClient.post<any>(this.serviceURL+`reprocessFiles`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public updateFile(file:File, moovFileId:number, moovStepInstanceId:number){
    let r = new UpdateFileRequest();
    r.moovFileId=moovFileId;
    r.moovStepInstanceId=moovStepInstanceId;
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.customerContext?.id;
    r.requestorEntityType=this.authService.customerContext?"C":undefined;
    let body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Updating file");
    return this.httpClient.post<any>(this.serviceURL+`updateFile`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public restoreFile(moovFileId:number, moovStepInstanceId:number){
    let r = new RestoreFileRequest();
    r.moovFileId=moovFileId;
    r.moovStepInstanceId=moovStepInstanceId;
    r.requestorId=this.authService.currentUserValue!.id;
    r.requestorEntityId=this.authService.customerContext?.id;
    r.requestorEntityType=this.authService.customerContext?"C":undefined;
    this.logger.log("Restoring file");
    return this.httpClient.post<any>(this.serviceURL+`restoreFile`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovExecutionHistory(moovDefinitionId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovExecutionHistory/` + moovDefinitionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovConfigurationExecutionHistory(moovConfigurationId:number, fromDateTime:Date, toDateTime:Date, statusFilter:number[], requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovConfigurationExecutionHistory/` + moovConfigurationId + '/' + fromDateTime.toISOString() + '/' + toDateTime.toISOString() + '/' + statusFilter.toString() + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getProcessingMoovInstances(requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`processingMoovInstances` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovStepInstanceStatus(moovStepInstanceId:number){
    return this.httpClient.get<any>(this.serviceURL+`moovStepInstanceStatus/` + moovStepInstanceId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovInstance(moovInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovInstance/` + moovInstanceId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovInstances(requestorId:number, moovInstanceIdList:number[], requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    let r = new MoovInstancesRequest();
    r.requestorId = requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovInstanceIdList = moovInstanceIdList;
    return this.httpClient.post<any>(this.serviceURL+`moovInstances`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovInstanceCollision(collisionId:string, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovInstanceCollision/` + collisionId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of ([this.createBasicResponseError(error)])}));
  }
  public getMoovInstanceUpdateTime(moovInstanceId:number){
    return this.httpClient.get<any>(this.serviceURL+`moovInstanceUpdateTime/` + moovInstanceId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public getMoovFile(moovFileId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`moovFile/` + moovFileId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public approveMoovStepInstance(requestorId:number, moovStepInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new ApproveMoovStepInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovStepInstanceId = moovStepInstanceId;
    this.logger.log("Approving moov step instance");
    return this.httpClient.post<any>(this.serviceURL+`approveMoovStepInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public executeMoovStepInstance(requestorId:number, moovStepInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new ExecuteMoovStepInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovStepInstanceId = moovStepInstanceId;
    this.logger.log("Executing moov step instance");
    return this.httpClient.post<any>(this.serviceURL+`executeMoovStepInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public runFailedMoovStepInstance(requestorId:number, moovStepInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new RunFailedMoovStepInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovStepInstanceId = moovStepInstanceId;
    this.logger.log("Running failed moov step instance");
    return this.httpClient.post<any>(this.serviceURL+`runFailedMoovStepInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public suspendMoovInstance(requestorId:number, moovInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new SuspendMoovInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovInstanceId = moovInstanceId;
    this.logger.log("Suspending moov instance");
    return this.httpClient.post<any>(this.serviceURL+`suspendMoovInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public resumeMoovInstance(requestorId:number, moovInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new ResumeMoovInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovInstanceId = moovInstanceId;
    this.logger.log("Resuming moov instance");
    return this.httpClient.post<any>(this.serviceURL+`resumeMoovInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public startMoovInstance(requestorId:number, moovInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new StartMoovInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovInstanceId = moovInstanceId;
    this.logger.log("Starting moov instance");
    return this.httpClient.post<any>(this.serviceURL+`startMoovInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteMoovInstance(requestorId:number, moovInstanceId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    let r = new DeleteMoovInstanceRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovInstanceId = moovInstanceId;
    this.logger.log("Deleting moov instance");
    return this.httpClient.post<any>(this.serviceURL+`deleteMoovInstance`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public resolveMoovInstanceCollision(requestorId:number, collisionId:string, requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    //this returns an array of Basic Responses
    let r = new ResolveMoovInstanceCollisionRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.collisionId = collisionId;
    this.logger.log("Resolving moov instance collision");
    return this.httpClient.post<any>(this.serviceURL+`resolveMoovInstanceCollision`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of ([this.createBasicResponseError(error)])}));
  }
  //#endregion

  //#region moov variables
  public getMoovVariables(requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    this.logger.log("Getting moov variables");
    return this.httpClient.get<any>(this.serviceURL+`moovVariables` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  public createMoovVariable(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, mv:MoovVariable):Observable<any>{
    this.logger.log("Creating moov variable");
    let r = new CreateMoovVariableRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovVariable = MoovVariable.mapToDTO(mv);
    return this.httpClient.post<any>(this.serviceURL+`createMoovVariable`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateMoovVariable(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, mv:MoovVariable):Observable<any>{
    this.logger.log("Updating moov variable");
    let r = new UpdateMoovVariableRequest();
    r.requestorId = requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovVariable = MoovVariable.mapToDTO(mv);
    return this.httpClient.put<any>(this.serviceURL+`updateMoovVariable`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteMoovVariable(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, moovVariableId:number):Observable<any>{
    this.logger.log("Deleting moov variable");
    let r=new DeleteMoovVariableRequest();
    r.requestorId=requestorId;
    r.requestorEntityId = requestorEntityId;
    r.requestorEntityType = requestorEntityType;
    r.moovVariableId = moovVariableId;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteMoovVariable`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region action items
  public getActionItemsCount(requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    this.logger.log("Getting action items count");
    return this.httpClient.get<any>(this.serviceURL+`actionItemsCount` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getActionItems(requestorEntityId:number|undefined, requestorEntityType:string|undefined):Observable<any>{
    this.logger.log("Getting action items");
    return this.httpClient.get<any>(this.serviceURL+`actionItems` + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public actionItemAction(requestorId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined, actionItemId:number, typeOfActionTaken:string, remarkForActionTaken:string):Observable<any>{
    this.logger.log("Action item action");
    let r = new ActionItemActionRequest();
    r.id=actionItemId;
    r.actionTakenByUserId=requestorId;
    r.requestorId=requestorId;
    r.requestorEntityId=requestorEntityId;
    r.requestorEntityType=requestorEntityType;
    r.typeOfActionTaken=typeOfActionTaken;
    r.remarkForActionTaken=remarkForActionTaken;
    return this.httpClient.post<any>(this.serviceURL+`actionItemAction`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region received files
  public getReceivedFile(receivedFileId:number, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`receivedFile/` + receivedFileId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }
  public getReceivedFiles(fromDateTime:Date, toDateTime:Date, statusFilter:string, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`receivedFiles/` + fromDateTime.toISOString() + '/' + toDateTime.toISOString() + '/' + statusFilter.toLowerCase() + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region executed moov instances
  public getExecutedMoovInstances(fromDateTime:Date, toDateTime:Date, statusFilter:number[], requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    return this.httpClient.get<any>(this.serviceURL+`executedMoovInstances/` + fromDateTime.toISOString() + '/' + toDateTime.toISOString() + '/' + statusFilter.toString() + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region billable moov data
  public getBillableMoovData(fromDateTime: Date, toDateTime: Date, requestorEntityId:number|undefined, requestorEntityType:string|undefined) {
    return this.httpClient.get<any>(this.serviceURL + `billableMoovData/` + fromDateTime.toISOString() + '/' + toDateTime.toISOString() + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }
  //#endregion

  //#region search files
  public searchFiles(fileName:string, startsWith:boolean, fromDateTime:Date, toDateTime:Date){
    return this.httpClient.get<any>(this.serviceURL + `searchFiles/` + fileName + '/' + startsWith + '/' + fromDateTime.toISOString() + '/' + toDateTime.toISOString(), this.optionsJson).pipe(catchError((error: any): any =>{this.handleError(error); return of(this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region premier services
  public createPremierServiceSchedule(pss:PremierServiceSchedule, requestorId:number){
    let r = new CreatePremierServiceScheduleRequest();
    r.premierServiceSchedule=PremierServiceSchedule.mapToDTO(pss);
    r.requestorId=requestorId;
    r.requestorEntityId=pss.customerId;
    r.requestorEntityType="C";
    return this.httpClient.post<any>(this.serviceURL+`createPremierServiceSchedule`, r, this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }

  public getPremierServiceExecutions(fromDateTime:Date, toDateTime:Date, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting premier service executions");
    return this.httpClient.get<any>(this.serviceURL + `premierServiceExecutions/` + fromDateTime.toISOString() + '/' + toDateTime.toISOString() + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }

  public getScheduledPremierServices(fromDateTime:Date, toDateTime:Date, requestorEntityId:number|undefined, requestorEntityType:string|undefined){
    this.logger.log("Getting scheduled premier services");
    return this.httpClient.get<any>(this.serviceURL + `scheduledPremierServices/` + fromDateTime.toISOString() + '/' + toDateTime.toISOString() + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }
  //#endregion

  //#region qbo
  public deleteQBEntriesForMoovStepInstance(moovStepInstanceId:number, requestorId:number, requestorCustomerId:number, clientId:number){
    this.logger.log("Deleting QB entries");
    let r=new DeleteQBEntriesForMoovStepInstanceRequest();
    r.moovStepInstanceId=moovStepInstanceId;
    r.requestorId=requestorId;
    r.requestorCustomerId=requestorCustomerId;
    r.clientId = clientId;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteQBEntriesForMoovStepInstance`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }
  //#endregion

  //#region xero
  public deleteXeroEntriesForMoovStepInstance(moovStepInstanceId:number, requestorId:number, requestorCustomerId:number, clientId:number){
    this.logger.log("Deleting Xero entries");
    let r=new DeleteXeroEntriesForMoovStepInstanceRequest();
    r.moovStepInstanceId=moovStepInstanceId;
    r.requestorId=requestorId;
    r.requestorCustomerId=requestorCustomerId;
    r.clientId = clientId;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteXeroEntriesForMoovStepInstance`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }
  //#endregion

  //#region microsoft
  public deleteMicrosoftCalendarEntriesForMoovStepInstance(moovStepInstanceId:number, requestorId:number, requestorCustomerId:number, clientId:number){
    this.logger.log("Deleting Microsoft Calendar entries");
    let r=new DeleteMicrosoftCalendarEntriesForMoovStepInstanceRequest();
    r.moovStepInstanceId=moovStepInstanceId;
    r.requestorId=requestorId;
    r.requestorCustomerId=requestorCustomerId;
    r.clientId = clientId;
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteMicrosoftCalendarEntriesForMoovStepInstance`, {body: r, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }
  //#endregion

  //#region time zone stuff
  public getTimeZoneById(id:string){
    this.logger.log("Getting time zone for " + id);
    return this.httpClient.get<any>(this.serviceURL + `timeZoneById/` + encodeURIComponent(id), this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }
  public getSupportedTimeZones(){
    this.logger.log("Getting supported time zones");
    return this.httpClient.get<any>(this.serviceURL + `supportedTimeZones`, this.optionsJson).pipe(catchError((error: any): any => { this.handleError(error); return of(this.createBasicResponseError(error)) }));
  }
  //#endregion

  private createBasicResponseError(error: HttpErrorResponse){
    let br = new BasicResponse();
    br.success=false;
    br.message='Backend error - please try again later';
    return br;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(()=>(error));
  }
}
